import { useState, useEffect, useCallback } from "react";
import Chart from "react-apexcharts";
import {
  Box, Grid, Card, CardContent, Typography, Stack, Chip,
  CircularProgress, TextField, Autocomplete, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from "@mui/material";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ImageIcon from "@mui/icons-material/Image";
import CategoryIcon from "@mui/icons-material/Category";
import SpeedIcon from "@mui/icons-material/Speed";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { PageHeaderCard } from "src/general_components/PageHeaderCard";
import { ocrAnalisisEstadisticas } from "../helpers/OcrData";

// ── SummaryCard ────────────────────────────────────────────────────────────────
const SummaryCard = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ flex: 1, minWidth: 180, borderRadius: 3 }} elevation={2}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{
        color: "white",
        bgcolor: color,
        borderRadius: 2,
        p: 1.2,
        display: "flex",
        fontSize: 28,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h4" fontWeight={800} color={color} lineHeight={1}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.disabled">{subtitle}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

// ── Tendencia ──────────────────────────────────────────────────────────────────
const Tendencia = ({ actual, anterior }) => {
  if (!anterior) return null;
  const diff = actual - anterior;
  const pct = Math.abs(Math.round((diff / anterior) * 100));
  if (diff > 0) return (
    <Stack direction="row" spacing={0.3} alignItems="center">
      <TrendingUpIcon fontSize="small" color="success" />
      <Typography variant="caption" color="success.main" fontWeight={600}>+{pct}%</Typography>
    </Stack>
  );
  if (diff < 0) return (
    <Stack direction="row" spacing={0.3} alignItems="center">
      <TrendingDownIcon fontSize="small" color="error" />
      <Typography variant="caption" color="error.main" fontWeight={600}>-{pct}%</Typography>
    </Stack>
  );
  return <TrendingFlatIcon fontSize="small" color="disabled" />;
};

// ── Chip confianza ─────────────────────────────────────────────────────────────
const ChipConfianza = ({ valor }) => {
  if (valor === null || valor === undefined) return <Chip label="—" size="small" />;
  const v = parseFloat(valor);
  return (
    <Chip
      label={`${v.toFixed(1)}%`}
      size="small"
      color={v >= 80 ? "success" : v >= 50 ? "warning" : "error"}
    />
  );
};

// ── Página principal ───────────────────────────────────────────────────────────
export function OcrEstadisticas() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Filtros
  const [fechaDesde, setFechaDesde] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [fechaHasta, setFechaHasta] = useState(() => new Date().toISOString().slice(0, 10));
  const [agrupacion, setAgrupacion] = useState("dia");
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);

  const agrupaciones = [
    { label: "Por día", value: "dia" },
    { label: "Por semana", value: "semana" },
    { label: "Por mes", value: "mes" },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    const res = await ocrAnalisisEstadisticas({
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
      agrupacion,
      id_plantilla: plantillaSeleccionada?.id || null,
    });
    setData(res);
    setLoading(false);
  }, [fechaDesde, fechaHasta, agrupacion, plantillaSeleccionada]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resumen = data?.resumen || {};
  const porPeriodo = data?.por_periodo || [];
  const porPlantilla = data?.por_plantilla || [];
  const porUsuario = data?.por_usuario || [];
  const dist = data?.distribucion_confianza || {};
  const ultimos = data?.ultimos || [];
  const plantillasDisponibles = data?.plantillas_disponibles || [];

  // ── Chart: evolución temporal ────────────────────────────────────────────────
  const chartEvolucion = {
    options: {
      chart: { type: "area", toolbar: { show: false }, fontFamily: "Roboto, sans-serif", animations: { enabled: true, speed: 600 } },
      stroke: { curve: "smooth", width: [2.5, 2] },
      fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.02 } },
      xaxis: {
        categories: porPeriodo.map(d => d.label),
        labels: { rotate: porPeriodo.length > 10 ? -30 : 0, style: { fontSize: "11px", colors: "#888" } },
        axisBorder: { show: false }, axisTicks: { show: false },
      },
      yaxis: { labels: { formatter: v => Math.round(v), style: { colors: "#888", fontSize: "11px" } }, min: 0 },
      colors: ["#1976d2", "#2e7d32"],
      legend: { position: "top", horizontalAlign: "left", fontSize: "13px" },
      tooltip: { shared: true, intersect: false, theme: "light" },
      dataLabels: { enabled: false },
      grid: { borderColor: "#f0f0f0", strokeDashArray: 4, xaxis: { lines: { show: false } } },
      markers: { size: porPeriodo.length <= 10 ? 4 : 0, hover: { size: 6 } },
    },
    series: [
      { name: "Análisis", data: porPeriodo.map(d => parseInt(d.total_analisis) || 0) },
      { name: "Imágenes", data: porPeriodo.map(d => parseInt(d.total_imagenes) || 0) },
    ],
  };

  // ── Chart: por plantilla ─────────────────────────────────────────────────────
  const PALETA = ["#1976d2","#ed6c02","#2e7d32","#9c27b0","#d32f2f","#0288d1","#7b1fa2","#388e3c"];
  const chartPlantillas = {
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
      plotOptions: { bar: { horizontal: true, borderRadius: 5, distributed: true, barHeight: "60%" } },
      colors: porPlantilla.map((_, i) => PALETA[i % PALETA.length]),
      xaxis: {
        categories: porPlantilla.map(p => p.nombre),
        labels: { style: { fontSize: "12px", colors: "#666" } },
        axisBorder: { show: false }, axisTicks: { show: false },
      },
      legend: { show: false },
      dataLabels: { enabled: true, offsetX: 6, style: { fontSize: "12px", fontWeight: 600, colors: ["#444"] }, formatter: v => v.toLocaleString("es-AR") },
      tooltip: { y: { formatter: v => `${v} análisis` }, theme: "light" },
      grid: { borderColor: "#f0f0f0", strokeDashArray: 4, yaxis: { lines: { show: false } } },
    },
    series: [{ name: "Análisis", data: porPlantilla.map(p => parseInt(p.total_analisis) || 0) }],
  };

  // ── Chart: distribución confianza ────────────────────────────────────────────
  const distAlta  = parseInt(dist.alta)    || 0;
  const distMedia = parseInt(dist.media)   || 0;
  const distBaja  = parseInt(dist.baja)    || 0;
  const chartDonut = {
    options: {
      chart: { type: "donut", fontFamily: "Roboto, sans-serif", animations: { enabled: true, speed: 600 } },
      labels: ["Alta (≥80%)", "Media (50-79%)", "Baja (<50%)"],
      colors: ["#2e7d32", "#ed6c02", "#d32f2f"],
      legend: { position: "bottom", fontSize: "13px" },
      plotOptions: { pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Total campos", fontSize: "13px", color: "#666" } } } } },
      dataLabels: { enabled: true, formatter: (val) => `${Math.round(val)}%` },
      tooltip: { y: { formatter: v => `${v.toLocaleString("es-AR")} campos` }, theme: "light" },
    },
    series: [distAlta, distMedia, distBaja],
  };

  // ── Chart: por usuario ───────────────────────────────────────────────────────
  const chartUsuarios = {
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
      plotOptions: { bar: { horizontal: true, borderRadius: 5, distributed: true, barHeight: "60%" } },
      colors: porUsuario.map((_, i) => PALETA[i % PALETA.length]),
      xaxis: {
        categories: porUsuario.map(u => u.nombre || `Usuario #${u.id_usuario}`),
        labels: { style: { fontSize: "12px", colors: "#666" } },
        axisBorder: { show: false }, axisTicks: { show: false },
      },
      legend: { show: false },
      dataLabels: { enabled: true, offsetX: 6, style: { fontSize: "12px", fontWeight: 600, colors: ["#444"] }, formatter: v => v.toLocaleString("es-AR") },
      tooltip: { y: { formatter: v => `${v} análisis` }, theme: "light" },
      grid: { borderColor: "#f0f0f0", strokeDashArray: 4, yaxis: { lines: { show: false } } },
    },
    series: [{ name: "Análisis", data: porUsuario.map(u => parseInt(u.total_analisis) || 0) }],
  };

  const ultimoPeriodo = porPeriodo[porPeriodo.length - 1];
  const penultimoPeriodo = porPeriodo.length >= 2 ? porPeriodo[porPeriodo.length - 2] : null;

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeaderCard title="Estadísticas OCR" icon={<QueryStatsIcon />} />

      {/* Filtros */}
      <Paper variant="outlined" sx={{ mx: 1, mb: 2, p: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" gap={1}>
          <TextField
            label="Desde"
            type="date"
            size="small"
            value={fechaDesde}
            onChange={e => setFechaDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            value={fechaHasta}
            onChange={e => setFechaHasta(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <Autocomplete
            size="small"
            options={agrupaciones}
            value={agrupaciones.find(a => a.value === agrupacion) || null}
            onChange={(_, v) => v && setAgrupacion(v.value)}
            getOptionLabel={o => o.label}
            disableClearable
            sx={{ minWidth: 150 }}
            renderInput={params => <TextField {...params} label="Agrupación" />}
          />
          <Autocomplete
            size="small"
            options={plantillasDisponibles}
            value={plantillaSeleccionada}
            onChange={(_, v) => setPlantillaSeleccionada(v)}
            getOptionLabel={o => o.nombre}
            sx={{ minWidth: 200 }}
            renderInput={params => <TextField {...params} label="Plantilla (opcional)" />}
          />
          <Button variant="contained" size="small" onClick={fetchData} disabled={loading}>
            {loading ? <CircularProgress size={16} /> : "Actualizar"}
          </Button>
        </Stack>
      </Paper>

      {loading && !data ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress size={48} />
          <Typography variant="body2" color="text.secondary" mt={2}>Cargando estadísticas...</Typography>
        </Box>
      ) : (
        <Box sx={{ px: 1 }}>
          {/* Summary cards */}
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }} gap={2}>
            <SummaryCard
              title="Análisis realizados"
              value={(parseInt(resumen.total_analisis) || 0).toLocaleString("es-AR")}
              icon={<AutoFixHighIcon fontSize="inherit" />}
              color="#1976d2"
            />
            <SummaryCard
              title="Imágenes procesadas"
              value={(parseInt(resumen.total_imagenes) || 0).toLocaleString("es-AR")}
              icon={<ImageIcon fontSize="inherit" />}
              color="#ed6c02"
            />
            <SummaryCard
              title="Campos extraídos"
              value={(parseInt(resumen.total_campos) || 0).toLocaleString("es-AR")}
              icon={<CategoryIcon fontSize="inherit" />}
              color="#7b1fa2"
            />
            <SummaryCard
              title="Confianza promedio"
              value={resumen.confianza_promedio ? `${parseFloat(resumen.confianza_promedio).toFixed(1)}%` : "—"}
              icon={<SpeedIcon fontSize="inherit" />}
              color={parseFloat(resumen.confianza_promedio) >= 80 ? "#2e7d32" : parseFloat(resumen.confianza_promedio) >= 50 ? "#ed6c02" : "#d32f2f"}
            />
          </Stack>

          {/* Último período resumen */}
          {ultimoPeriodo && (
            <Card sx={{ mb: 3, borderRadius: 3, bgcolor: "#f8faff", border: "1px solid #e3eaf5" }} elevation={0}>
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Último período: <strong>{ultimoPeriodo.label}</strong>
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{parseInt(ultimoPeriodo.total_analisis)} análisis</Typography>
                    <Tendencia actual={parseInt(ultimoPeriodo.total_analisis)} anterior={penultimoPeriodo ? parseInt(penultimoPeriodo.total_analisis) : 0} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{parseInt(ultimoPeriodo.total_imagenes)} imágenes</Typography>
                    <Tendencia actual={parseInt(ultimoPeriodo.total_imagenes)} anterior={penultimoPeriodo ? parseInt(penultimoPeriodo.total_imagenes) : 0} />
                  </Stack>
                  {ultimoPeriodo.confianza_promedio && (
                    <ChipConfianza valor={ultimoPeriodo.confianza_promedio} />
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Gráfico evolución */}
          {porPeriodo.length > 0 && (
            <Card sx={{ mb: 3, borderRadius: 3 }} elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={0.5}>Evolución temporal</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Análisis e imágenes procesadas por {agrupacion === "dia" ? "día" : agrupacion === "semana" ? "semana" : "mes"}
                </Typography>
                <Chart options={chartEvolucion.options} series={chartEvolucion.series} type="area" height={280} />
              </CardContent>
            </Card>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Distribución confianza */}
            {(distAlta + distMedia + distBaja) > 0 && (
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, height: "100%" }} elevation={2}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={0.5}>Confianza de extracción</Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Distribución de {(distAlta + distMedia + distBaja).toLocaleString("es-AR")} campos
                    </Typography>
                    <Chart options={chartDonut.options} series={chartDonut.series} type="donut" height={280} />
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Por plantilla */}
            {porPlantilla.length > 0 && (
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, height: "100%" }} elevation={2}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={0.5}>Por plantilla</Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Top {porPlantilla.length} plantillas más utilizadas
                    </Typography>
                    <Chart
                      options={chartPlantillas.options}
                      series={chartPlantillas.series}
                      type="bar"
                      height={Math.max(220, porPlantilla.length * 44 + 60)}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Por usuario */}
            {porUsuario.length > 0 && (
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, height: "100%" }} elevation={2}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={0.5}>Por usuario</Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Top {porUsuario.length} usuarios con mayor actividad
                    </Typography>
                    <Chart
                      options={chartUsuarios.options}
                      series={chartUsuarios.series}
                      type="bar"
                      height={Math.max(220, porUsuario.length * 44 + 60)}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Tabla últimos análisis */}
          {ultimos.length > 0 && (
            <Card sx={{ borderRadius: 3 }} elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>Últimos análisis</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "rgba(0,0,0,0.04)" }}>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Plantilla</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>Imágenes</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>Confianza</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ultimos.map(u => (
                        <TableRow key={u.id} hover>
                          <TableCell>
                            <Typography variant="caption" color="text.disabled">#{u.id}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(u.created_at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={u.plantilla} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{u.usuario || "—"}</Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <Chip label={u.total_imagenes} size="small" />
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <ChipConfianza valor={u.confianza_prom} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {!loading && ultimos.length === 0 && (
            <Card sx={{ borderRadius: 3 }} elevation={1}>
              <CardContent sx={{ textAlign: "center", py: 6 }}>
                <AutoFixHighIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                <Typography color="text.secondary">
                  No hay análisis OCR registrados en el período seleccionado.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
}
